import { useRef, useState } from 'react';
import { useAuthStore } from '@/entities/session/model/authStore';
import { connectionsApi, type ConnectionChoiceItem } from '../connectionPicker/connectionsApi';
import { OctoApiError } from '@/shared/api/http/errors';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { LicenseSummary } from '@/features/shell/api/shellInfoApi';
import { classifyLoginError, type LoginErrorInfo } from './loginError';
import { licenseNotice, type LicenseNotice } from './licenseGate';
import { useLoginGate } from './gateStore';

export interface LoginFormValues {
  userCode: string;
  password: string;
  remember: boolean;
}

/**
 * Login akışı: aday bağlantılar (0 → hata, 1 → otomatik, >1 → seçim) → auth/login →
 * lisans kapısı.
 *
 * Lisans kapısı girişi ENGELLEMEZ; kullanıcıyı içeri almadan önce durumu söyler
 * (abonelik bitti/bitiyor, web lisansı yok → el terminali görünümü). Onay verilene
 * kadar yönlendirme bekletilir; bkz. `licenseGate`.
 */
export function useLogin() {
  const login = useAuthStore((s) => s.login);
  const [candidates, setCandidates] = useState<ConnectionChoiceItem[] | null>(null);
  const [selected, setSelected] = useState<ConnectionChoiceItem | null>(null);
  const [error, setError] = useState<LoginErrorInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<LicenseNotice | null>(null);
  const setPending = useLoginGate((s) => s.setPending);
  /* `notice` durumu bir sonraki çizime kadar okunamıyor; kapının açık kalıp
     kalmayacağına aynı akış içinde karar verilmesi gerek. */
  const hasNoticeRef = useRef(false);

  async function submit(values: LoginFormValues, invalidMessage: string) {
    setError(null);
    setLoading(true);
    // Akış bitene kadar rota koruyucusu da beklesin (bkz. gateStore).
    setPending(true);
    try {
      let connection = selected;
      if (!connection) {
        let choice: Awaited<ReturnType<typeof connectionsApi.loginCandidates>> | null = null;
        try {
          choice = await connectionsApi.loginCandidates(values.userCode, values.password);
        } catch (e) {
          // Tek bağlantılı kurulumda uç 404 dönebilir → header'sız login
          if (!(e instanceof OctoApiError && e.status === 404)) throw e;
        }
        if (choice?.connectionRequired) {
          if (choice.items.length === 0) {
            setError(classifyLoginError(invalidMessage));
            return;
          }
          if (choice.items.length > 1) {
            setCandidates(choice.items);
            return;
          }
          connection = choice.items[0] ?? null;
        }
      }
      await login({
        userCode: values.userCode,
        password: values.password,
        remember: values.remember,
        connectionId: connection?.connectionId ?? null,
        connectionName: connection?.name ?? null,
      });
      await runLicenseGate();
    } catch (e) {
      setError(classifyLoginError(e));
    } finally {
      setLoading(false);
      // Uyarı varsa kapı açık kalır; "Devam et" kapatır.
      if (!hasNoticeRef.current) setPending(false);
    }
  }

  /**
   * Abonelik bilgisi Support'a çıkıyor ve DAL'da önbellekli; yine de girişin bu
   * çağrıya BAĞLANMAMASI gerekir — ulaşılamazsa kullanıcı normal şekilde girer.
   */
  async function runLicenseGate() {
    const user = useAuthStore.getState().user;
    if (!user) return;
    let summary: LicenseSummary | null = null;
    try {
      summary = await apiClient.get<LicenseSummary>(ENDPOINTS.SYSTEM_LICENSE_SUMMARY, {
        octo: { expectSuccess: false },
      });
    } catch {
      /* abonelik bilgisi alınamadı: kapı açık */
    }
    const next = licenseNotice({
      hasWebLicense: user.hasWebLicense,
      hasNodeLicense: user.hasNodeLicense,
      expirationDate: summary?.available ? summary.expirationDate : null,
    });
    hasNoticeRef.current = next != null;
    setNotice(next);
  }

  return {
    submit,
    candidates,
    selected,
    setSelected,
    error,
    loading,
    notice,
    /** Kullanıcı uyarıyı okudu → yönlendirme serbest */
    acknowledgeNotice: () => {
      hasNoticeRef.current = false;
      setNotice(null);
      setPending(false);
    },
    resetCandidates: () => setCandidates(null),
  };
}
